import { MouseEvent, ReactNode, useCallback, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Container, Tree } from 'rsuite'
import OutTemplateModel, { TOutTemplate } from '@/client/models/outTemplate'
import { Button } from '@/client/components/Controls'
import { useLayout, TContextMenuItem } from '@/client/components/Layout/LayoutManager'
import EditTemplate from '@client/views/projects/out/EditTemplate'
import UpdateTemplate from './UpdateTemplate'
import { OutTemplateListIcon } from '@/client/config'
import { Capabilities, hasPermission } from '@/shared'
import project from '@/client/stores/projectStore'
import { TTreeNodeItem, TreeNodeProps, TreeTopProps } from '@client/stores/tabsStore'
import { useContextMenuTree, ContextTarget } from '@/client/utils/useContextMenu'
import { createDefaultListContextMenuItems, findTreeNodeById } from '@/client/utils/listContextMenuDefaults'
import { toastError, toastSuccess } from '@client/utils/toastMessage.tsx'
import ModalDialog from '@client/components/ModalDialog.tsx'
import OutTableColumnModel from '@client/models/outTableColumn.ts'
import useSidebarInvalidation from '@client/utils/useSidebarInvalidation.ts'
import useListContextMenuItems from '@client/utils/useListContextMenuItems.ts'

const treeTopStyle = {
    // borderBottom: '1px solid var(--color-4)',
    // padding: '0.5em 0',
    // display: 'flex',
    // flexFlow: 'row nowrap',
    // width: '100%',
    // alignItems: 'center',
    // justifyContent: 'space-between',
}
type TSelectedContextMenu = {
    id: string
    label: string
    type: string
}

export default function OutTemplateLists() {
    const { t } = useTranslation()
    const { addTab, NoProjectSelected, onContextMenu, openTabToRight, removeTabCopies, removeOtherTabs } = useLayout()
    const [openEditDialog, setOpenEditDialog] = useState<boolean>(false)
    const [type, setType] = useState<string>('edit')
    const [id, setId] = useState<string>('')
    const { data, loading } = project.useTemplates()
    const hasData = data && data?.length > 0
    const hasProject = Boolean(project.currentProject.value?._id)

    const [contextMenu, setContextMenu] = useState<TSelectedContextMenu | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)
    const outTableModel = new OutTableColumnModel()
    const refetchTemplates = () => {
        project.templates.value = null
    }
    useSidebarInvalidation({
        tables: ['template', 'outTemplate'],
        actions: ['updated', 'deleted'],
        refetch: refetchTemplates,
    })
    // fetch all out tables from this project
    const treeNodes = useMemo(() => {
        if (data?.length == 0) return []
        const templates: any = { label: t('Templates'), value: 'templates', children: [] as any[] }
        data && data.forEach((item: TOutTemplate) => {
            const child: any = {
                label: `${item.name} - ${item.excel}`,
                value: item.excel,
                children: [],
                parent: templates,
            }
            //console.log(item.sheets)
            item.sheets.forEach((sheet) => {
                child.children.push({
                    id: sheet._id,
                    label: sheet._id,
                    value: `${sheet._id} - ${sheet.name}`,
                    parent: child,
                    resourceId: sheet._id,
                } as never)
            })
            templates.children.push(child as never)
        })
        return [templates]
    }, [data, loading, t])

    const handleUpload = (uploadResult: any) => {
        console.log(uploadResult)
        setTimeout(refetchTemplates, 100)
    }

    const updloadDialogComponent = openEditDialog
        ? ReactDOM.createPortal(
            <UpdateTemplate
                db={project?.currentProject.value?._id || ''}
                id={id}
                type={type}
                setOpenDialog={setOpenEditDialog}
                onUpload={handleUpload}
            />,
            document.body
        )
        : null

    const controller = {
        getTabContent(params: any) {
            const { activeKey, tab } = params
            return <EditTemplate key={activeKey} tabId={tab.id} />
        },

        removeTab: function (params: any) {
            return true
        },
    }

    function onAddTemplate(event: MouseEvent<HTMLElement>): void {
        setOpenEditDialog(true)
        setType('add')
        setId('')
    }

    function newTab(node: TTreeNodeItem) {
        return {
            dataId: node.id, // sheet id
            name: node.label,
            component: node.id,
            icon: <OutTemplateListIcon />,
            controller,
            store: {
                data: node,
                formData: null,
                formRef: null,
                resourceId: String((node as any).resourceId || ''),
            },
        }
    }

    function onOpenTab(node: TTreeNodeItem) {
        addTab(newTab(node))
    }

    function TreeTop({ data }: TreeTopProps) {
        return (
            <Container className="tree-top" style={treeTopStyle}>
                {hasPermission(Capabilities.template_write) && (
                    <Button appearance="ghost" onClick={onAddTemplate}>
                        {t('Upload template')}
                    </Button>
                )}
            </Container>
        )
    }

    function TreeNode({ node }: TreeNodeProps) {
        if (node.children) return node.label
        return node.value
    }

    function EmptyTree() {
        return <div className="empty-tree-list">{loading ? t('Loading ...') : t('The list is empty.')}</div>
    }

    function onSelectTreeItem(activeNode: any): void {
        const hasChildren = (activeNode?.children?.length || 0) > 0
        if (!hasChildren) onOpenTab(activeNode)
    }

    const getNodeById = useCallback(
        (id: string): TTreeNodeItem | null => {
            return (findTreeNodeById(treeNodes, id) as TTreeNodeItem) || null
        },
        [treeNodes]
    )

    const extraItems = useMemo<TContextMenuItem[]>(() => {
        if (!hasPermission(Capabilities.template_write)) return []

        return [
            {
                label: t('Delete'),
                onClick: (_evt, current) => {
                    setContextMenu({
                        id: current.id,
                        label: current.label,
                        type: 'delete',
                    })
                },
            },
        ]
    }, [t, contextMenu, hasPermission(Capabilities.template_write)])

    const { show } = useListContextMenuItems<TTreeNodeItem>({
        t,
        onContextMenu,
        addTab,
        openTabToRight,
        removeTabCopies,
        removeOtherTabs,
        getItemById: getNodeById,
        createTab: newTab,
        extraItems,
        includeSeparatorBeforeExtra: true,
    })

    const handleContextMenuCapture = useContextMenuTree(treeNodes, show)

    const deleteDialogButtons = [
        { status: 'delete', label: t('Delete') },
        { status: 'cancel', label: t('Cancel') },
    ]

    function handleDeleteDialogButtons(status: string) {
        if (!contextMenu) return true

        if (status === 'cancel') {
            setContextMenu(null)
            return true
        }

        if (status === 'delete') {
            const projectId = project.currentProject.value?._id || ''
            if (!projectId) {
                toastError(t('No project selected.'))
                return true
            }

            const outTableId = contextMenu.id
            setIsDeleting(true)

            outTableModel
                .delete(projectId, outTableId)
                .then(() => {
                    toastSuccess(t('Template sheet deleted successfully.'))

                    setTimeout(refetchTemplates, 100)
                })
                .catch((err: any) => {
                    console.error('Failed to delete template sheet:', err)
                    toastError(t('Failed to delete template sheet.'))
                })
                .finally(() => {
                    setIsDeleting(false)
                    setContextMenu(null)
                })

            return false // keep dialog open while request runs
        }

        setContextMenu(null)
        return true
    }

    const deleteDialog = contextMenu && (
        <ModalDialog
            id="template-sheet-delete-dialog"
            title={t('Delete {{name}}?', { name: contextMenu.label })}
            buttons={deleteDialogButtons}
            onClick={handleDeleteDialogButtons}
            size="sm"
        >
            <p>{t('Are you sure you want to delete this template sheet?')}</p>
            <p>{t('WARNING! This will delete all related resources (converter, mapping, data lists).')}</p>
            <p>{t('This action cannot be undone.')}</p>
        </ModalDialog>
    )

    return !hasProject ? (
        NoProjectSelected()
    ) : (
        <div className="sidebar-content sidebar-content-project-out-templates">
            <div className="sidebar-content-body"
                onContextMenuCapture={handleContextMenuCapture}
            >
                <TreeTop data={data} />

                {hasData ? (
                    <Tree
                        className="sidebar-content-tree"
                        data={treeNodes}
                        defaultExpandItemValues={['templates']}
                        renderTreeNode={(node) => <TreeNode node={node} />}
                        onSelect={onSelectTreeItem}
                    />
                ) : (
                    <EmptyTree />
                )}
            </div>
            {updloadDialogComponent}
            {deleteDialog}
        </div>
    )
}
